import React from 'react';
import { useTranslation } from 'react-i18next';

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}

/**
 * IMPORTANT: The aria-label state suffix ("checked" / "not checked") only works
 * in **controlled** mode — i.e. when `checked={bool}` is passed as a prop.
 * Using `defaultChecked` (uncontrolled) leaves `props.checked` as `undefined`,
 * so the aria-label will permanently read "not checked" regardless of state.
 * Always pass `checked={bool}` (not `defaultChecked`) for correct SR announcements.
 */
const Checkbox: React.FC<CheckboxProps> = ({ label, 'aria-label': externalAriaLabel, ...props }) => {
  const { t } = useTranslation();
  // Include the label and checked state in aria-label so screen readers (NVDA/JAWS)
  // announce the full context on every state change, not just the first focus.
  // Without this, NVDA only reads "checked"/"not checked" on toggle, dropping the label name.
  // Use t() so Spanish/other locale users hear the translated state word, not English.
  const ariaLabel = label
    ? `${label}, ${props.checked ? t('common.checked', 'checked') : t('common.notChecked', 'not checked')}`
    : (externalAriaLabel ?? undefined);

  return (
    <label className="inline-flex items-center">
      <input
        type="checkbox"
        className="form-checkbox h-4 w-4 text-primary border-border rounded focus:ring-ring"
        aria-label={ariaLabel}
        {...props}
      />
      {label && <span className="ml-2 text-text" aria-hidden="true">{label}</span>}
    </label>
  );
};

export default Checkbox;
